// Where a shared note is read, as this client recognises it.
//
// Pure: no React, no fetch, no browser API beyond the string it is handed - the
// same split `shell/screens.ts` and `day/day-model.ts` keep, and it matters more
// here than usual because this is the function that decides whether the whole
// application shell is mounted at all (`main.tsx`).
//
// THIS FILE READS A URL AND NEVER WRITES ONE. The server authors share links -
// `POST /notes/:id/share` returns the whole `url`, built from `PUBLIC_BASE_URL`
// and `SHARE_PATH_PREFIX` in api/src/http/constants.ts - because a share link is
// pasted into somebody else's chat window and has to keep working, so two
// clients composing their own from a token would publish two different links for
// one note. What this client has to do is recognise one when the browser arrives
// at it, and that is all this file does.
//
// SO THE PATH IS SPELLED TWICE, once on each side of a boundary the linter will
// not let anything cross (`web/**` may not import `api/src/**`). The pair is
// pinned by a test on each side that names the literal `/n/`:
// `web/test/share-path.test.ts` here, and the "is spelled into a URL by the
// SERVER" case in `api/test/note-share.routes.test.ts` there. Move one alone and
// its own side goes red.

/** The prefix a shared note is read at. Must equal the server's `SHARE_PATH_PREFIX`. */
export const SHARE_PATH_PREFIX = '/n/';

const ENCODED_ROUTE_BYTE = /%[0-9a-f]{2}/gi;

function routeMatchPath(path: string): string {
  return path.replace(ENCODED_ROUTE_BYTE, (encoded) => {
    const character = String.fromCharCode(Number.parseInt(encoded.slice(1), 16));
    return /^[a-z0-9/-]$/i.test(character) ? character : encoded;
  });
}

/**
 * The token in a path, or null when the path is not a shared note at all.
 *
 * NULL IS THE ANSWER THAT MATTERS, because it is what mounts the ordinary
 * application: every other path in this client belongs to the shell
 * (`shell/screens.ts`), which corrects an unknown one to Today. Every path in
 * the `/n/` namespace stays on the public page, including an empty or malformed
 * one; the server answers those with the same 404 as a revoked token.
 *
 * The token is NOT validated here. Its shape is the server's business, and a
 * client that rejected a token the server would have accepted would be a
 * second, older opinion about what a token looks like. Keeping malformed paths
 * public also prevents them from mounting the shell and asking for account data.
 */
export function tokenAt(path: string): string | null {
  const normalized = routeMatchPath(path);
  const stem = SHARE_PATH_PREFIX.slice(0, -1);
  if (normalized !== stem && !normalized.startsWith(SHARE_PATH_PREFIX)) return null;
  const token = normalized === stem ? '' : normalized.slice(SHARE_PATH_PREFIX.length);
  // A trailing slash is somebody's mail client being helpful, not part of the
  // token. Additional path segments remain malformed token input and are sent
  // to the public endpoint for its ordinary 404.
  const trimmed = token.endsWith('/') ? token.slice(0, -1) : token;
  // Pasted links arrive percent-encoded surprisingly often - a chat client that
  // "helpfully" escapes the URL, a redirect that round-trips it. `newShareToken`
  // produces only url-safe characters, so decoding one that was never encoded is
  // a no-op, and decoding one that was is the difference between a working link
  // and a 404 nobody can explain.
  try {
    return decodeURIComponent(trimmed);
  } catch {
    // Keep a malformed segment on the public page. The API will refuse the
    // encoded value with the same 404 as every other wrong token.
    return trimmed;
  }
}
